import { useState, useRef, useMemo, useCallback, useEffect } from "react";
import html2pdf from "html2pdf.js";
import { can, SC, MT_BADGE, MT_LABELS, MT_OPTIONS, classifyMaintenanceType, maintenanceTypeOf, CLOSE_BACKDATE_DAYS, closeDateWindow, formatTime12 } from "../config";
import { maintenanceTypes, maintenanceTypeLabel } from "../maintenanceTypesCache";
import { useBackToClose } from "../hooks/useBackToClose";
import { listDrafts, getDraft, saveDraft, deleteDraft, purgeLocalDrafts, formatDraftStamp, draftWho } from "../drafts";
import { apiCall, getAuthHeaders } from "../api";
import { Btn, TimeField } from "./common/FormElements";
import { FileUploadBox } from "./common/FormElements";

const BLANK_LOG = {
  startDate: "", startTime: "", endDate: "", endTime: "", closeNow: false,
  description: "", detail: "", rootCause: "", rectification: "",
  sparePart: "", spare_part_id: "", supervisor: "",
  workType: "internal",
  maintenanceType: "",   // "" = auto-detect from the description
  vendorName: "", poNumber: "", poDate: "", poAmount: "", paymentMode: "po",
  reportFile: null, reportFileName: "",
  beforeReportFile: null, beforeReportFileName: "",
  poFile: null, poFileName: "",
};

// ---------------------------------------------------------------------------
// Drafts for a part-filled New Maintenance Log.
//
// Every detail field on this form is mandatory, so a half-filled log cannot be
// submitted at all — if the supervisor is called away, the typing is lost. The form
// is autosaved to this browser instead, per machine, and restored when the form is
// reopened. Uploads survive too: FileUploadBox stores the uploaded file's path, not
// the File object, so the whole form is plain JSON.
//
// Drafts are deliberately local. A draft is not a maintenance record — it must never
// reach the history collection, where it would land in reports, MTBF and the audit
// trail as a real entry.
// ---------------------------------------------------------------------------
// The key carries the user as well as the unit and machine. Without it a draft was
// shared by everyone who signed in on that PC: a supervisor's half-written log came
// back under the next person's login, with their own name about to be signed against
// it. Shop-floor terminals are shared, so this was the normal case, not an edge one.
// Fields that are a choice rather than entered data. Picking Internal/External or a
// payment mode is not work worth preserving, and treating it as such meant that merely
// opening the form and choosing a work type left a draft behind — which then reopened
// past the Work Type step on the next visit.
const DRAFT_CHOICE_KEYS = new Set(["workType", "paymentMode", "maintenanceType", "closeNow"]);

// Only keep a draft once something has actually been entered, so merely opening and
// closing the form does not leave one behind.
function draftHasContent(form) {
  return Object.keys(BLANK_LOG).some(k => {
    if (DRAFT_CHOICE_KEYS.has(k)) return false;
    const now = form[k], blank = BLANK_LOG[k];
    if (typeof blank === "boolean") return !!now !== blank;
    return String(now ?? "") !== String(blank ?? "");
  });
}


export async function openProtectedFile(ev, filePath, fileName) {
  ev.preventDefault();
  if (!filePath) return;
  try {
    const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/files/${filePath}`, {
      headers: { ...getAuthHeaders() }
    });
    if (!res.ok) {
      let detail = "";
      try { detail = (await res.json()).detail || ""; } catch {}
      throw new Error(detail || `HTTP ${res.status}`);
    }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const displayName = fileName || (filePath.split("/").pop() || "file");
    const mime = blob.type || "";
    const isImage = mime.startsWith("image/");
    const isPdf = mime === "application/pdf" || displayName.toLowerCase().endsWith(".pdf");

    const overlay = document.createElement("div");
    overlay.style.cssText = "position:fixed;inset:0;background:rgba(0,0,0,0.85);z-index:99999;display:flex;flex-direction:column;align-items:center;justify-content:center;padding:20px;font-family:sans-serif;";

    const header = document.createElement("div");
    header.style.cssText = "width:100%;max-width:1200px;display:flex;justify-content:space-between;align-items:center;color:white;margin-bottom:12px;";
    const title = document.createElement("span");
    title.textContent = displayName;
    title.style.cssText = "font-weight:600;font-size:14px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;max-width:60%;";
    header.appendChild(title);

    const btnGroup = document.createElement("div");
    btnGroup.style.cssText = "display:flex;gap:8px;";
    const dlBtn = document.createElement("button");
    dlBtn.textContent = "Download";
    dlBtn.style.cssText = "background:#2563eb;color:white;border:none;padding:6px 14px;border-radius:6px;cursor:pointer;font-size:13px;";
    dlBtn.onclick = (e) => {
      e.stopPropagation();
      const a = document.createElement("a");
      a.href = url; a.download = displayName;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
    };
    const closeBtn = document.createElement("button");
    closeBtn.textContent = "Close";
    closeBtn.style.cssText = "background:#6b7280;color:white;border:none;padding:6px 14px;border-radius:6px;cursor:pointer;font-size:13px;";
    btnGroup.appendChild(dlBtn);
    btnGroup.appendChild(closeBtn);
    header.appendChild(btnGroup);
    overlay.appendChild(header);

    const content = document.createElement("div");
    content.style.cssText = "flex:1;width:100%;max-width:1200px;background:white;border-radius:8px;overflow:auto;display:flex;align-items:center;justify-content:center;";
    if (isImage) {
      const img = document.createElement("img");
      img.src = url;
      img.style.cssText = "max-width:100%;max-height:100%;object-fit:contain;";
      content.appendChild(img);
    } else if (isPdf) {
      const iframe = document.createElement("iframe");
      iframe.src = url;
      iframe.style.cssText = "width:100%;height:100%;border:none;";
      content.appendChild(iframe);
    } else {
      const msg = document.createElement("div");
      msg.style.cssText = "padding:40px;text-align:center;color:#6b7280;";
      msg.innerHTML = "<p>Preview not supported for this file type.</p><p style='margin-top:8px;font-size:13px;'>Click Download to save the file to your computer.</p>";
      content.appendChild(msg);
    }
    overlay.appendChild(content);

    const cleanup = () => {
      if (overlay.parentNode) document.body.removeChild(overlay);
      document.removeEventListener("keydown", escHandler);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    };
    const escHandler = (e) => { if (e.key === "Escape") cleanup(); };
    document.addEventListener("keydown", escHandler);
    closeBtn.onclick = cleanup;
    overlay.onclick = (e) => { if (e.target === overlay) cleanup(); };

    document.body.appendChild(overlay);
  } catch (err) {
    alert("Failed to open file: " + (err.message || "Unknown error"));
  }
}

// Opens a protected-file viewer that can hold MULTIPLE files (e.g. Before + After)
// with Prev / Next navigation. Each item: { path, name, label }.
async function openProtectedGallery(ev, items) {
  if (ev && ev.preventDefault) ev.preventDefault();
  const list = (items || []).filter(it => it && it.path);
  if (!list.length) return;

  const overlay = document.createElement("div");
  overlay.style.cssText = "position:fixed;inset:0;background:rgba(0,0,0,0.85);z-index:99999;display:flex;flex-direction:column;align-items:center;justify-content:center;padding:20px;font-family:sans-serif;";

  const header = document.createElement("div");
  header.style.cssText = "width:100%;max-width:1200px;display:flex;justify-content:space-between;align-items:center;color:white;margin-bottom:12px;gap:12px;";
  const title = document.createElement("span");
  title.style.cssText = "font-weight:600;font-size:14px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;flex:1;";
  header.appendChild(title);
  const btnGroup = document.createElement("div");
  btnGroup.style.cssText = "display:flex;gap:8px;align-items:center;";
  const dlBtn = document.createElement("button");
  dlBtn.textContent = "Download";
  dlBtn.style.cssText = "background:#2563eb;color:white;border:none;padding:6px 14px;border-radius:6px;cursor:pointer;font-size:13px;";
  const closeBtn = document.createElement("button");
  closeBtn.textContent = "Close";
  closeBtn.style.cssText = "background:#6b7280;color:white;border:none;padding:6px 14px;border-radius:6px;cursor:pointer;font-size:13px;";
  btnGroup.appendChild(dlBtn);
  btnGroup.appendChild(closeBtn);
  header.appendChild(btnGroup);
  overlay.appendChild(header);

  const content = document.createElement("div");
  content.style.cssText = "flex:1;width:100%;max-width:1200px;background:white;border-radius:8px;overflow:auto;display:flex;align-items:center;justify-content:center;";
  overlay.appendChild(content);

  const nav = document.createElement("div");
  nav.style.cssText = "width:100%;max-width:1200px;display:flex;justify-content:center;align-items:center;gap:16px;color:white;margin-top:12px;";
  const prevBtn = document.createElement("button");
  prevBtn.textContent = "‹ Prev";
  prevBtn.style.cssText = "background:#374151;color:white;border:none;padding:8px 18px;border-radius:6px;cursor:pointer;font-size:14px;";
  const counter = document.createElement("span");
  counter.style.cssText = "font-size:13px;";
  const nextBtn = document.createElement("button");
  nextBtn.textContent = "Next ›";
  nextBtn.style.cssText = "background:#374151;color:white;border:none;padding:8px 18px;border-radius:6px;cursor:pointer;font-size:14px;";
  nav.appendChild(prevBtn);
  nav.appendChild(counter);
  nav.appendChild(nextBtn);
  if (list.length > 1) overlay.appendChild(nav);

  let idx = 0;
  const urls = [];
  const cleanup = () => {
    if (overlay.parentNode) document.body.removeChild(overlay);
    document.removeEventListener("keydown", keyHandler);
    setTimeout(() => urls.forEach(u => u && URL.revokeObjectURL(u)), 1000);
  };

  async function render() {
    const it = list[idx];
    const displayName = it.name || (it.path.split("/").pop() || "file");
    title.textContent = (it.label ? it.label + " — " : "") + displayName;
    counter.textContent = (idx + 1) + " of " + list.length;
    content.innerHTML = "<div style='color:#6b7280;padding:40px;'>Loading…</div>";
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/files/${it.path}`, { headers: { ...getAuthHeaders() } });
      if (!res.ok) { let d = ""; try { d = (await res.json()).detail || ""; } catch {} throw new Error(d || `HTTP ${res.status}`); }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      urls.push(url);
      const mime = blob.type || "";
      const isImage = mime.startsWith("image/");
      const isPdf = mime === "application/pdf" || displayName.toLowerCase().endsWith(".pdf");
      content.innerHTML = "";
      if (isImage) {
        const img = document.createElement("img"); img.src = url; img.style.cssText = "max-width:100%;max-height:100%;object-fit:contain;"; content.appendChild(img);
      } else if (isPdf) {
        const iframe = document.createElement("iframe"); iframe.src = url; iframe.style.cssText = "width:100%;height:100%;border:none;"; content.appendChild(iframe);
      } else {
        const msg = document.createElement("div"); msg.style.cssText = "padding:40px;text-align:center;color:#6b7280;"; msg.innerHTML = "<p>Preview not supported. Click Download.</p>"; content.appendChild(msg);
      }
      dlBtn.onclick = (e) => { e.stopPropagation(); const a = document.createElement("a"); a.href = url; a.download = displayName; document.body.appendChild(a); a.click(); document.body.removeChild(a); };
    } catch (err) {
      content.innerHTML = "<div style='color:#b91c1c;padding:40px;text-align:center;'>Failed to open: " + (err.message || "error") + "</div>";
    }
  }

  prevBtn.onclick = (e) => { e.stopPropagation(); idx = (idx - 1 + list.length) % list.length; render(); };
  nextBtn.onclick = (e) => { e.stopPropagation(); idx = (idx + 1) % list.length; render(); };
  const keyHandler = (e) => {
    if (e.key === "Escape") cleanup();
    else if (e.key === "ArrowRight" && list.length > 1) { idx = (idx + 1) % list.length; render(); }
    else if (e.key === "ArrowLeft" && list.length > 1) { idx = (idx - 1 + list.length) % list.length; render(); }
  };
  document.addEventListener("keydown", keyHandler);
  closeBtn.onclick = cleanup;
  overlay.onclick = (e) => { if (e.target === overlay) cleanup(); };
  document.body.appendChild(overlay);
  render();
}

function AddLogModal({ machine, onSave, onClose, unitId, editEntry = null, spareParts = [], user = null, resume = false }) {
  // Drafts apply to new logs only — an entry being edited is already saved in the
  // database, so there is nothing to lose. Read once, before any state that uses it.
  // Drafts are shared and live on the server, so unlike a local one this arrives after
  // mount. The form starts blank and is filled in when it lands.
  const draftsEnabled = !editEntry && !!unitId && !!machine?.id;
  const [restoredDraft, setRestoredDraft] = useState(null);
  const [draftSavedAt, setDraftSavedAt] = useState(null);
  const [draftVersion, setDraftVersion] = useState(null);
  const [draftConflict, setDraftConflict] = useState("");
  const [draftSaving, setDraftSaving] = useState(false);

  // A new log always starts at Work Type. Restoring the step a draft was left on
  // skipped that question entirely, so the Internal/External choice looked missing.
  // The draft still fills the fields — only the step is not carried over.
  const [step, setStep] = useState(editEntry ? 2 : 1);
  const [form, setForm] = useState(editEntry ? {
    ...BLANK_LOG,
    startDate: editEntry.startDate || "",
    startTime: editEntry.startTime || "",
    endDate: editEntry.endDate || "",
    endTime: editEntry.endTime || "",
    closeNow: false,
    description: editEntry.description || "",
    detail: editEntry.detail || "",
    rootCause: editEntry.rootCause || "",
    rectification: editEntry.rectification || "",
    sparePart: editEntry.sparePart || "",
    spare_part_id: editEntry.spare_part_id || "",
    supervisor: editEntry.supervisor || "",
    workType: editEntry.workType || "internal",
    maintenanceType: editEntry.maintenanceType || "",
    vendorName: editEntry.vendorName || "",
    poNumber: editEntry.poNumber || "",
    poDate: editEntry.poDate || "",
    poAmount: editEntry.poAmount || "",
    paymentMode: editEntry.paymentMode === "cash" ? "cash" : "po",
    reportFile: editEntry.reportFilePath || null,
    reportFileName: editEntry.reportFileName || "",
    beforeReportFile: editEntry.beforeReportFilePath || null,
    beforeReportFileName: editEntry.beforeReportFileName || "",
    poFile: editEntry.poFilePath || null,
    poFileName: editEntry.poFileName || "",
  } : { ...BLANK_LOG, ...(restoredDraft?.form || {}) });
  const [saving, setSaving] = useState(false);
  const f = (key, val) => setForm(p => ({ ...p, [key]: val }));

  const draftVersionRef = useRef(null);
  useEffect(() => { draftVersionRef.current = draftVersion; }, [draftVersion]);
  const lastSavedRef = useRef("");

  // Pull this machine's shared draft once, and clear out the old per-browser ones.
  useEffect(() => {
    purgeLocalDrafts();
    if (!draftsEnabled) return;
    let cancelled = false;
    getDraft(unitId, machine.id).then(d => {
      if (cancelled || !d) return;
      setRestoredDraft(d);
      setDraftVersion(d.version);
      draftVersionRef.current = d.version;
      lastSavedRef.current = JSON.stringify({ form: { ...BLANK_LOG, ...(d.form || {}) }, step });
      setDraftSavedAt(d.updatedAt);
      setForm(prev => ({ ...prev, ...(d.form || {}) }));
      // Opened from the "Draft in progress" badge: go straight back to where the work
      // was left, filled in. A brand-new log still starts at Work Type, so the
      // Internal/External question is never skipped when there is nothing to resume.
      if (resume && d.step) setStep(d.step);
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftsEnabled, unitId, machine?.id]);

  // Autosave, debounced so typing does not hit the server on every keystroke.
  //
  // The version is held in a ref rather than a dependency. As a dependency it made the
  // effect self-feeding: a save changed the version, which re-ran the effect, which
  // scheduled another save, for ever — and two of those overlapping produced a version
  // clash with yourself, reported as a colleague having edited the draft.
  useEffect(() => {
    if (!draftsEnabled || saving) return;
    if (!draftHasContent(form)) return;   // nothing typed yet; never create an empty draft
    const snapshot = JSON.stringify({ form, step });
    if (snapshot === lastSavedRef.current) return;   // unchanged since the last save
    const t = setTimeout(async () => {
      // Re-check on the way out: pressing Save as Draft may have written this exact
      // state while the timer was pending, and repeating it is a wasted round trip.
      if (snapshot === lastSavedRef.current) return;
      const res = await saveDraft(unitId, machine.id, {
        form, step, version: draftVersionRef.current,
        machineCode: machine.machineNo, machineName: machine.name,
      });
      if (res.ok) {
        lastSavedRef.current = snapshot;
        draftVersionRef.current = res.draft.version;
        setDraftVersion(res.draft.version);
        setDraftSavedAt(res.draft.updatedAt);
        setDraftConflict("");
      } else if (res.conflict) {
        setDraftConflict(res.message);
      }
    }, 800);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, step, saving, draftsEnabled, unitId, machine?.id]);

  // Closing within the debounce window would drop the last keystrokes. A request cannot
  // be awaited during unmount, so the save is fired first and the close follows.
  const latestRef = useRef({ form, step });
  latestRef.current = { form, step };
  const committedRef = useRef(false);
  useEffect(() => () => {
    if (!draftsEnabled || committedRef.current) return;
    const { form: lastForm, step: lastStep } = latestRef.current;
    if (draftHasContent(lastForm)) {
      saveDraft(unitId, machine.id, {
        form: lastForm, step: lastStep, version: draftVersionRef.current,
        machineCode: machine.machineNo, machineName: machine.name,
      });
    }
    // draftVersion is deliberately NOT a dependency. With it here, every save changed the
    // version, React tore the effect down, and the teardown fired another save — one that
    // nothing was awaiting, so the server's version moved on without the client knowing
    // and every save after it came back as a clash with yourself.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftsEnabled, unitId, machine?.id]);

  // Saves and stays put. It used to close the form, which stopped the obvious next
  // move — save what the office has typed so far, then go on to attach the photo.
  // Closing is what the ✕ is for, and closing already saves anyway.
  async function saveDraftNow() {
    if (!draftsEnabled || !draftHasContent(form)) return;
    setDraftSaving(true);
    const snapshot = JSON.stringify({ form, step });
    const res = await saveDraft(unitId, machine.id, {
      form, step, version: draftVersionRef.current,
      machineCode: machine.machineNo, machineName: machine.name,
    });
    if (res.ok) {
      lastSavedRef.current = snapshot;      // stops the autosave repeating this write
      draftVersionRef.current = res.draft.version;
      setDraftVersion(res.draft.version);
      setDraftSavedAt(res.draft.updatedAt);
      setDraftConflict("");
    } else if (res.conflict) {
      setDraftConflict(res.message);
    }
    setDraftSaving(false);
  }

  async function discardDraft() {
    if (!window.confirm("Discard this shared draft and start a blank log? It will be removed for everyone.")) return;
    committedRef.current = true;
    await deleteDraft(unitId, machine?.id);
    setRestoredDraft(null);
    setDraftSavedAt(null);
    setForm({ ...BLANK_LOG });
    setStep(1);
  }
  // Back closes this form rather than navigating away and losing what is typed.
  useBackToClose(onClose);

  const isExternal = form.workType === "external";
  // Internal work only asks for PO details once a spare part is involved — either picked
  // from inventory (spare_part_id) or typed by hand (sparePart).
  const hasSparePart = !!(form.spare_part_id || (form.sparePart || "").trim());
  const showInternalPO = !isExternal && hasSparePart;
  // Every detail field on this form is mandatory. The vendor / PO block only applies when
  // the work is External, or when an internal job bought a part against a PO — those are
  // required whenever that block is on screen, and irrelevant otherwise.
  const filled = v => String(v ?? "").trim() !== "";
  const poBlockShown = isExternal || showInternalPO;
  // Cash has no purchase order, so its number, date and document are not asked for.
  // The amount stays in the same field either way, so every total picks cash up too.
  const paidInCash = form.paymentMode === "cash";
  // While the machine is still down nobody knows the cause or what fixed it, so those
  // are asked for at the close instead — either closing now, or editing a closed entry.
  const willBeClosed = !!form.closeNow || !!(editEntry?.endDate || "").trim();
  const missingStep2 = [
    [!filled(form.startDate), "Date"],
    [!filled(form.startTime), "Time"],
    [!filled(form.description), "Description of Incident / Event"],
    // The server asks for this too. Without it the repair reads as instant and
    // contributes nothing to MTTR.
    [willBeClosed && !filled(form.endTime), "Close Time"],
    [willBeClosed && !filled(form.detail), "Detail of Incident / Event"],
    [willBeClosed && !filled(form.rootCause), "Root Cause"],
    [willBeClosed && !filled(form.rectification), "Rectification / Maintenance Detail"],
    [!filled(form.supervisor), "Sign. Maint. Supervisor"],
    [poBlockShown && !filled(form.vendorName), "Vendor / Agency Name"],
    [poBlockShown && !paidInCash && !filled(form.poNumber), "Purchase Order No."],
    [poBlockShown && !paidInCash && !filled(form.poDate), "PO Date"],
    [poBlockShown && !(Number(form.poAmount) > 0), paidInCash ? "Cash Amount" : "PO Amount"],
  ].filter(([bad]) => bad).map(([, label]) => label);
  const canProceedStep2 = missingStep2.length === 0;
  const isEditing = !!editEntry;

  // Draft controls sit in the footer beside Back / Next on every step of a new log.
  // There is nothing to save until something has been entered, hence the disabled state.
  const draftDirty = draftsEnabled && draftHasContent(form);
  const draftControls = isEditing ? null : (
    <div className="flex items-center gap-2 flex-wrap justify-center">
      {draftSavedAt && !draftConflict && (
        <span className="hidden sm:inline text-[11px] text-gray-400" data-testid="draft-saved-note">
          Shared draft saved {formatDraftStamp(draftSavedAt)}
          {restoredDraft && <> &middot; {draftWho(restoredDraft)}</>}
        </span>
      )}
      {draftConflict && (
        <span className="text-[11px] font-semibold text-red-600" data-testid="draft-conflict">
          {draftConflict}
        </span>
      )}
      {(restoredDraft || draftSavedAt) && (
        <button onClick={discardDraft} className="text-xs font-medium text-gray-400 hover:text-red-600"
          data-testid="discard-draft-btn" title="Discard the saved draft and start a blank log">
          Start fresh
        </button>
      )}
      <button onClick={saveDraftNow} disabled={!draftDirty || draftSaving}
        className="rounded-lg border border-blue-300 bg-white px-4 py-2 text-sm font-semibold text-blue-700 transition hover:bg-blue-50 disabled:opacity-40 disabled:cursor-not-allowed"
        data-testid="save-draft-btn"
        title={draftDirty ? "Save what is filled in and carry on — nothing is submitted yet" : "Nothing filled in to save yet"}>
        {draftSaving ? "Saving…" : "Save as Draft"}
      </button>
    </div>
  );

  const todayStr = new Date().toISOString().split("T")[0];
  // Any entry that is not "Scheduled PM" / "Repair & Maintenance" is a breakdown —
  // including faults typed by their own name (e.g. "Spring Broken", "Key Jam").
  const isSuperAdmin = (user?.role === "superadmin");
  // The dates anyone else may close on — today or the day before (CLOSE_BACKDATE_DAYS)
  // after, never in the future. Same rule the API enforces.
  const closeWin = closeDateWindow(form.startDate, todayStr);
  const closeDefault = todayStr > closeWin.max ? closeWin.max
                     : (todayStr < closeWin.min ? closeWin.min : todayStr);
  const minDateStr = "2000-01-01";

  function isValidDate(dateStr) {
    if (!dateStr) return true;
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return false;
    const year = d.getFullYear();
    return year >= 2000 && year <= new Date().getFullYear() + 1;
  }

  // A typed date gets past the picker: min/max on <input type="date"> bound the
  // calendar, not what can be keyed in. 2026-10-23 on an entry opened 2026-10-07 is
  // exactly that, and nothing stopped it until the server refused the save.
  const closeDateOutOfWindow = !isSuperAdmin && form.isClosed && form.endDate
    && (form.endDate < closeWin.min || form.endDate > closeWin.max);

  async function handleSave() {
    if (!isValidDate(form.startDate)) { alert("Start date is invalid or out of range (2000 \u2013 next year)."); return; }
    if (closeDateOutOfWindow) {
      alert(`Close date ${form.endDate} is outside the allowed window (${closeWin.min} to ${closeWin.max}). `
        + `An entry is closed today or the day before, whenever it opened. `
        + `Ask a Super Admin to close it outside that.`);
      setStep(2);
      return;
    }
    if (!isValidDate(form.poDate)) { alert("PO date is invalid or out of range (2000 \u2013 next year)."); return; }
    // Detail fields are mandatory on both new entries and edits.
    if (missingStep2.length) { alert(`Please fill in: ${missingStep2.join(", ")}.`); setStep(2); return; }
    // Phase-based evidence: OPENING a breakdown needs the BEFORE photo; CLOSING needs the AFTER photo.
    if (!isEditing) {
      if (!form.beforeReportFile) { alert("Please upload the BEFORE maintenance photo / report to open this breakdown."); return; }
    } else {
      // An older entry may pre-date the BEFORE photo rule and that image cannot be produced
      // after the fact, so it is only enforced when the entry already has one — it must not
      // be removed.
      if (editEntry.beforeReportFilePath && !form.beforeReportFile) {
        alert("The BEFORE maintenance photo / report cannot be removed from this entry."); return;
      }
      if (form.closeNow) {
        if (!form.endTime) { alert("Please enter the close time."); return; }
        if (!form.reportFile) { alert("Please upload the AFTER maintenance (work done) photo / report to close this breakdown."); return; }
      }
    }
    // The PO document is required wherever the vendor / PO block applies.
    if (poBlockShown && !paidInCash && !form.poFile) { alert("Please upload the Purchase Order (PO) Document."); return; }
    setSaving(true);
    try {
      const logData = {
        machineId: machine.id, startDate: form.startDate, startTime: form.startTime,
        endDate: form.closeNow ? (form.endDate || todayStr) : form.endDate,
        endTime: form.endTime, closeNow: !!form.closeNow,
        description: form.description,
        detail: form.detail, rootCause: form.rootCause, rectification: form.rectification,
        sparePart: form.sparePart, spare_part_id: form.spare_part_id || "", supervisor: form.supervisor, workType: form.workType,
        maintenanceType: form.maintenanceType || "",
        vendorName: form.vendorName, poNumber: form.poNumber, poDate: form.poDate,
        poAmount: form.poAmount, paymentMode: form.paymentMode || "po",
        reportFilePath: form.reportFile, reportFileName: form.reportFileName,
        beforeReportFilePath: form.beforeReportFile, beforeReportFileName: form.beforeReportFileName,
        poFilePath: form.poFile, poFileName: form.poFileName,
      };
      if (isEditing) { await apiCall("PUT", `/history/${unitId}/${editEntry.id}`, logData); }
      else { await apiCall("POST", `/history/${unitId}`, logData); }

      // Auto-record spare part usage if selected from inventory (only on new entry, not edit)
      if (!isEditing && form.spare_part_id && form.spare_part_id !== "__custom__") {
        try {
          await apiCall("POST", `/spare-parts/${unitId}/${form.spare_part_id}/use`, {
            spare_part_id: form.spare_part_id,
            quantity: 1,
            machine_name: machine?.name || "",
            machine_id: machine?.id || "",
            history_entry_id: form.id || "",
            remarks: `Used during maintenance: ${form.description || ""}`,
          });
        } catch (err) {
          console.error("Failed to record spare part usage:", err);
        }
      }

      // The entry is in the database now, so the draft has served its purpose.
      committedRef.current = true;
      await deleteDraft(unitId, machine?.id);
      onSave();
    } catch (error) {
      // The API answers a rejected save with the reason — which date, which window,
      // who can override. Swallowing it and printing "Failed to save log entry" is why
      // a Unit-III supervisor retried a close date the server was never going to take.
      const detail = error?.response?.data?.detail;
      alert(typeof detail === "string" && detail.trim()
        ? detail
        : "Failed to save log entry. Please check the fields and try again.");
    }
    finally { setSaving(false); }
  }

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[92vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="add-log-modal">
        <div className="p-5 border-b border-gray-100">
          <div className="flex items-start justify-between">
            <div>
              <h2 className="font-bold text-gray-900 text-lg">{isEditing ? "Edit Maintenance Log" : "New Maintenance Log"}</h2>
              <p className="text-xs text-gray-400 mt-0.5">{machine.name} &middot; {machine.machineNo} &middot; MNT-F-04</p>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none mt-0.5" data-testid="close-log-modal">&times;</button>
          </div>
          <div className="flex items-center gap-2 mt-4">
            {(isEditing ? [["2", "Log Details"], ["3", "Upload Files"]] : [["1", "Work Type"], ["2", "Log Details"], ["3", "Upload Files"]]).map(([n, l], i) => {
              const stepNum = isEditing ? i + 2 : i + 1;
              return (
                <div key={n} className="flex items-center gap-2">
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${step > stepNum ? "bg-green-500 text-white" : step === stepNum ? "bg-blue-600 text-white" : "bg-gray-100 text-gray-400"}`}>
                    {step > stepNum ? "\u2713" : n}
                  </div>
                  <span className={`text-xs font-medium ${step === stepNum ? "text-blue-600" : "text-gray-400"}`}>{l}</span>
                  {i < (isEditing ? 1 : 2) && <div className="w-6 h-px bg-gray-200 shrink-0" />}
                </div>
              );
            })}
          </div>
        </div>

        <div className="p-5 space-y-5">
          {step === 1 && (
            <div className="space-y-4">
              <p className="text-sm font-medium text-gray-700">Was this maintenance done internally or by an external vendor?</p>
              <div className="grid grid-cols-2 gap-4">
                {[{ val: "internal", title: "Internal Work", desc: "Done by in-house maintenance team." },
                  { val: "external", title: "External Work", desc: "Done by outside vendor / agency." }].map(opt => (
                  <button key={opt.val} onClick={() => f("workType", opt.val)}
                    className={`text-left p-4 rounded-xl border-2 transition-all ${form.workType === opt.val ? "border-blue-500 bg-blue-50" : "border-gray-200 hover:border-gray-300 bg-white"}`}
                    data-testid={`work-type-${opt.val}`}>
                    <div className={`font-semibold text-sm mb-1 ${form.workType === opt.val ? "text-blue-700" : "text-gray-800"}`}>{opt.title}</div>
                    <div className="text-xs text-gray-500 leading-relaxed">{opt.desc}</div>
                  </button>
                ))}
              </div>
              {/* No draft controls on this step: picking Internal/External is not work
                  worth saving, and there is nothing else on the step to lose. */}
              <div className="flex justify-end">
                <button onClick={() => setStep(2)} className="bg-blue-600 text-white px-5 py-2 rounded-lg text-sm font-medium hover:bg-blue-700" data-testid="next-step-2">Next: Details &rarr;</button>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4">
              <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold ${isExternal ? "bg-orange-100 text-orange-700" : "bg-blue-100 text-blue-700"}`}>
                {isExternal ? "External Work" : "Internal Work"}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2 text-xs font-semibold text-gray-500 uppercase tracking-wide">Start of Incident / Event</div>
                <div><label className="text-xs text-gray-500">Date *</label><input type="date" className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm mt-0.5" min={minDateStr} max={todayStr} value={form.startDate} onChange={e => f("startDate", e.target.value)} data-testid="start-date" /></div>
                <div><label className="text-xs text-gray-500">Time *</label><TimeField value={form.startTime} onChange={v => f("startTime", v)} testId="start-time" /></div>
                {isEditing && (
                  <>
                    <div className="col-span-2 text-xs font-semibold text-gray-500 uppercase tracking-wide mt-1">End of Incident / Event</div>
                    {editEntry.endDate ? (
                      isSuperAdmin ? (
                        <div className="col-span-2 space-y-2">
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className="text-xs text-gray-500">Close Date <span className="text-amber-600 font-medium">(Super Admin correction)</span></label>
                              <input type="date" value={form.endDate} onChange={e => f("endDate", e.target.value)} className="w-full border border-amber-300 rounded-lg px-3 py-1.5 text-sm mt-0.5" data-testid="close-date-edit" />
                            </div>
                            <div>
                              <label className="text-xs text-gray-500">Close Time <span className="text-amber-600 font-medium">(Super Admin correction)</span></label>
                              <TimeField value={form.endTime} onChange={v => f("endTime", v)} testId="close-time-edit" />
                            </div>
                          </div>
                          <p className="text-xs text-amber-600 mt-1">Super Admin: correct the close date/time if it was entered wrong.</p>
                        </div>
                      ) : (
                        <div className="col-span-2 text-sm bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-gray-700">
                          Closed on <span className="font-semibold">{editEntry.endDate}{editEntry.endTime ? ` ${formatTime12(editEntry.endTime)}` : ""}</span>
                        </div>
                      )
                    ) : (
                      <div className="col-span-2 space-y-2">
                        <label className="flex items-center gap-2 text-sm text-gray-700">
                          <input type="checkbox" checked={form.closeNow} onChange={e => f("closeNow", e.target.checked)} data-testid="close-now" />
                          Mark this breakdown as <span className="font-semibold">Closed</span>
                        </label>
                        {form.closeNow && (
                          <div>
                            {isSuperAdmin ? (
                              <>
                                <div className="grid grid-cols-2 gap-3">
                                  <div>
                                    <label className="text-xs text-gray-500">Close Date * <span className="text-amber-600 font-medium">(Super Admin — any date)</span></label>
                                    <input
                                      type="date"
                                      min={form.startDate || minDateStr}
                                      max={todayStr}
                                      value={form.endDate || todayStr}
                                      onChange={e => f("endDate", e.target.value)}
                                      className="w-full border border-amber-300 rounded-lg px-3 py-1.5 text-sm mt-0.5"
                                      data-testid="close-date"
                                    />
                                  </div>
                                  <div>
                                    <label className="text-xs text-gray-500">Close Time *</label>
                                    <TimeField value={form.endTime} onChange={v => f("endTime", v)} testId="close-time" />
                                  </div>
                                </div>
                                <p className="text-xs text-amber-600 mt-1">
                                  Super Admin: any date from the start date ({form.startDate || "\u2014"}) up to today. Everyone else is limited to today or the day before.
                                </p>
                              </>
                            ) : (
                              /* One window whatever the work was. It used to be today or
                                 yesterday for a breakdown and today alone for everything
                                 else, so planned work finished on Friday could not be
                                 written up on Monday at all. */
                              <>
                                <div className="grid grid-cols-2 gap-3">
                                  <div>
                                    <label className="text-xs text-gray-500">Close Date *</label>
                                    <input
                                      type="date"
                                      min={closeWin.min}
                                      max={closeWin.max}
                                      value={form.endDate || closeDefault}
                                      onChange={e => f("endDate", e.target.value)}
                                      className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm mt-0.5"
                                      data-testid="close-date"
                                    />
                                  </div>
                                  <div>
                                    <label className="text-xs text-gray-500">Close Time *</label>
                                    <TimeField value={form.endTime} onChange={v => f("endTime", v)} testId="close-time" />
                                  </div>
                                </div>
                                <p className="text-xs text-gray-500 mt-1">
                                  Close it today or the day before &mdash; {closeWin.min} or {closeWin.max}. A Super Admin can close it on any other date.
                                </p>
                                {closeDateOutOfWindow && (
                                  <p className="mt-1 text-xs font-medium text-red-600" data-testid="close-date-out-of-window">
                                    {form.endDate} is outside that window — this entry cannot be saved closed.
                                    Pick a date between {closeWin.min} and {closeWin.max}, or ask a Super Admin.
                                  </p>
                                )}
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div><label className="text-xs text-gray-500">Description of Incident / Event *</label><input className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm mt-0.5" placeholder="e.g. Brake failure, Scheduled PM" value={form.description} onChange={e => f("description", e.target.value)} data-testid="log-description" /></div>
                <div>
                  <label className="text-xs text-gray-500">Maintenance Type</label>
                  <select className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm mt-0.5 bg-white"
                    value={form.maintenanceType || ""} onChange={e => f("maintenanceType", e.target.value)} data-testid="log-maintenance-type">
                    <option value="">Auto &mdash; {MT_LABELS[classifyMaintenanceType(form.description, editEntry?.logType)]}</option>
                    {/* The list comes from Settings, so a type added there is usable
                        straight away. Falls back to the built-in four if it is empty. */}
                    {(maintenanceTypes().length ? maintenanceTypes().map(t => t.key) : MT_OPTIONS)
                      .map(t => <option key={t} value={t}>{maintenanceTypeLabel(t) || MT_LABELS[t] || t}</option>)}
                  </select>
                  <p className="text-xs text-gray-400 mt-0.5">
                    Decides how this entry is counted in reports. Auto reads the description; pick a type to override it.
                  </p>
                </div>
                <div><label className="text-xs text-gray-500">Detail of Incident / Event {willBeClosed && <span className="text-red-500">*</span>}</label><input className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm mt-0.5" placeholder="Describe the problem in detail" value={form.detail} onChange={e => f("detail", e.target.value)} data-testid="log-detail" /></div>
                <div><label className="text-xs text-gray-500">Root Cause {willBeClosed && <span className="text-red-500">*</span>}</label><input className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm mt-0.5" placeholder="What caused this?" value={form.rootCause} onChange={e => f("rootCause", e.target.value)} data-testid="log-root-cause" /></div>
                <div><label className="text-xs text-gray-500">Rectification / Maintenance Detail {willBeClosed && <span className="text-red-500">*</span>}</label><input className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm mt-0.5" placeholder="What was done?" value={form.rectification} onChange={e => f("rectification", e.target.value)} data-testid="log-rectification" /></div>
                <div>
                  <label className="text-xs text-gray-500">Spare Part Used (if any)</label>
                  <select
                    className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm mt-0.5 bg-white"
                    value={form.spare_part_id || ""}
                    onChange={e => {
                      const partId = e.target.value;
                      if (partId === "") {
                        f("spare_part_id", "");
                        f("sparePart", "");
                      } else if (partId === "__custom__") {
                        f("spare_part_id", "");
                      } else {
                        const selected = (spareParts || []).find(sp => sp.id === partId);
                        f("spare_part_id", partId);
                        f("sparePart", selected ? `${selected.description}${selected.size ? ' (' + selected.size + ')' : ''}` : "");
                      }
                    }}
                    data-testid="spare-part-select"
                  >
                    <option value="">-- None --</option>
                    {(spareParts || []).map(sp => (
                      <option key={sp.id} value={sp.id}>
                        {sp.description}{sp.size ? ` (${sp.size})` : ""} — Stock: {sp.availQty}
                      </option>
                    ))}
                    <option value="__custom__">Other (type manually)</option>
                  </select>
                  {(!form.spare_part_id || form.spare_part_id === "") && (
                    <input
                      className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm mt-1"
                      placeholder="Type part name manually if not in list..."
                      value={form.sparePart || ""}
                      onChange={e => f("sparePart", e.target.value)}
                      data-testid="spare-part-manual"
                    />
                  )}
                </div>
                <div><label className="text-xs text-gray-500">Sign. Maint. Supervisor *</label><input className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm mt-0.5" placeholder="Supervisor name" value={form.supervisor} onChange={e => f("supervisor", e.target.value)} data-testid="log-supervisor" /></div>
              </div>
              {showInternalPO && (
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 space-y-3" data-testid="internal-po-section">
                  <p className="text-xs font-semibold text-blue-700 uppercase tracking-wide">{paidInCash ? "Cash Purchase Details" : "Purchase Order Details"}</p>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xs text-gray-500">Paid by</span>
                    {[["po", "Purchase Order"], ["cash", "Cash"]].map(([val, label]) => (
                      <button key={val} type="button" onClick={() => f("paymentMode", val)}
                        className={`px-3 py-1 rounded-lg text-xs font-semibold border transition-all ${form.paymentMode === val ? "bg-white text-gray-900 border-gray-400 shadow-sm" : "border-transparent text-gray-500 hover:bg-white/60"}`}
                        data-testid={`payment-mode-${val}`}>{label}</button>
                    ))}
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><label className="text-xs text-gray-500">Vendor / Agency Name *</label><input className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm mt-0.5" placeholder="e.g. Impress Engineering" value={form.vendorName} onChange={e => f("vendorName", e.target.value)} data-testid="internal-po-vendor" /></div>
                    {!paidInCash && <div><label className="text-xs text-gray-500">Purchase Order No. *</label><input className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm mt-0.5" placeholder="e.g. PO-2025-0142" value={form.poNumber} onChange={e => f("poNumber", e.target.value)} data-testid="internal-po-number" /></div>}
                    {!paidInCash && <div><label className="text-xs text-gray-500">PO Date *</label><input type="date" className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm mt-0.5" min={minDateStr} max={todayStr} value={form.poDate} onChange={e => f("poDate", e.target.value)} data-testid="internal-po-date" /></div>}
                    <div><label className="text-xs text-gray-500">{paidInCash ? "Cash Amount" : "PO Amount"} *</label><input type="number" className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm mt-0.5" placeholder="0.00" value={form.poAmount} onChange={e => f("poAmount", e.target.value)} data-testid="internal-po-amount" /></div>
                  </div>
                </div>
              )}
              {isExternal && (
                <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 space-y-3">
                  <p className="text-xs font-semibold text-orange-700 uppercase tracking-wide">External Vendor Details</p>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xs text-gray-500">Paid by</span>
                    {[["po", "Purchase Order"], ["cash", "Cash"]].map(([val, label]) => (
                      <button key={val} type="button" onClick={() => f("paymentMode", val)}
                        className={`px-3 py-1 rounded-lg text-xs font-semibold border transition-all ${form.paymentMode === val ? "bg-white text-gray-900 border-gray-400 shadow-sm" : "border-transparent text-gray-500 hover:bg-white/60"}`}
                        data-testid={`payment-mode-${val}`}>{label}</button>
                    ))}
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><label className="text-xs text-gray-500">Vendor / Agency Name *</label><input className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm mt-0.5" placeholder="e.g. Impress Engineering" value={form.vendorName} onChange={e => f("vendorName", e.target.value)} /></div>
                    {!paidInCash && <div><label className="text-xs text-gray-500">Purchase Order No. *</label><input className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm mt-0.5" placeholder="e.g. PO-2025-0142" value={form.poNumber} onChange={e => f("poNumber", e.target.value)} /></div>}
                    {!paidInCash && <div><label className="text-xs text-gray-500">PO Date *</label><input type="date" className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm mt-0.5" min={minDateStr} max={todayStr} value={form.poDate} onChange={e => f("poDate", e.target.value)} /></div>}
                    <div><label className="text-xs text-gray-500">{paidInCash ? "Cash Amount" : "PO Amount"} *</label><input type="number" className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm mt-0.5" placeholder="0.00" value={form.poAmount} onChange={e => f("poAmount", e.target.value)} /></div>
                  </div>
                </div>
              )}
              {/* A disabled button with no reason is maddening — name what is missing. */}
              {missingStep2.length > 0 && (
                <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" data-testid="missing-fields">
                  Still needed before this can be saved: <strong>{missingStep2.join(", ")}</strong>
                </div>
              )}
              <div className="flex items-center justify-between gap-3 flex-wrap">
                {!isEditing && <button onClick={() => setStep(1)} className="border border-gray-300 text-gray-600 px-4 py-2 rounded-lg text-sm hover:bg-gray-50">&larr; Back</button>}
                {isEditing && <div></div>}
                {draftControls}
                <button onClick={() => {
                  if (closeDateOutOfWindow) {
                    alert(`Close date ${form.endDate} is outside ${closeWin.min} to ${closeWin.max}. Fix it before attaching files.`);
                    return;
                  }
                  setStep(3);
                }}
                  className={`px-5 py-2 rounded-lg text-sm font-medium ${canProceedStep2 ? "bg-blue-600 text-white hover:bg-blue-700" : "border border-blue-300 bg-white text-blue-700 hover:bg-blue-50"}`}
                  title={canProceedStep2 ? "" : "You can attach the BEFORE photo now; the fields above are still needed before this can be saved."}
                  data-testid="next-step-3">Next: Files &rarr;</button>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-4">
              <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold ${isExternal ? "bg-orange-100 text-orange-700" : "bg-blue-100 text-blue-700"}`}>
                {isExternal ? "External Work" : "Internal Work"}
              </div>
              {!isEditing && (
                <FileUploadBox label="Before Maintenance — Report / Evidence *" file={form.beforeReportFile} fileName={form.beforeReportFileName}
                  onChange={f => setForm(p => ({ ...p, beforeReportFile: f?.path || null, beforeReportFileName: f?.name || "" }))}
                  onView={form.beforeReportFile ? () => openProtectedFile({ preventDefault() {} }, form.beforeReportFile, form.beforeReportFileName) : null}
                  accept=".pdf,.jpg,.jpeg,.png" hint="Required to OPEN the breakdown — photo/PDF of the machine condition BEFORE the work" />
              )}
              {isEditing && (
                <>
                  <FileUploadBox label="Before Maintenance — Report / Evidence" file={form.beforeReportFile} fileName={form.beforeReportFileName}
                    onChange={f => setForm(p => ({ ...p, beforeReportFile: f?.path || null, beforeReportFileName: f?.name || "" }))}
                    onView={form.beforeReportFile ? () => openProtectedFile({ preventDefault() {} }, form.beforeReportFile, form.beforeReportFileName) : null}
                    accept=".pdf,.jpg,.jpeg,.png" hint="Captured when the breakdown was opened" />
                  <FileUploadBox label={form.closeNow ? "After Maintenance — Report / Evidence (Work Done) *" : "After Maintenance — Report / Evidence (Work Done)"} file={form.reportFile} fileName={form.reportFileName}
                    onChange={f => setForm(p => ({ ...p, reportFile: f?.path || null, reportFileName: f?.name || "" }))}
                    onView={form.reportFile ? () => openProtectedFile({ preventDefault() {} }, form.reportFile, form.reportFileName) : null}
                    accept=".pdf,.jpg,.jpeg,.png" hint={form.closeNow ? "Required to CLOSE the breakdown — photo/PDF of the work-done report" : "Upload when you close the breakdown"} />
                </>
              )}
              {poBlockShown && (
                <FileUploadBox
                  label={paidInCash ? "Cash Bill / Receipt (optional)" : "Purchase Order (PO) Document *"}
                  file={form.poFile} fileName={form.poFileName}
                  onChange={f => setForm(p => ({ ...p, poFile: f?.path || null, poFileName: f?.name || "" }))}
                  accept=".pdf,.jpg,.jpeg,.png"
                  hint={paidInCash ? "Attach the cash bill or receipt if you have one" : "Required — upload the Purchase Order PDF / scan"} />
              )}
              <div className="bg-gray-50 rounded-xl p-4 text-xs text-gray-500 space-y-1">
                <p className="font-semibold text-gray-600">Log Summary</p>
                <p>Date: {form.startDate} {form.startTime} &rarr; {(isEditing && editEntry.endDate) ? `${editEntry.endDate} ${editEntry.endTime}` : (form.closeNow ? "will close now" : "Open")}</p>
                <p>Event: {form.description}</p>
                {form.sparePart && <p>Spare Part: {form.sparePart}</p>}
                <p>Supervisor: {form.supervisor}</p>
                {isExternal && <><p>Vendor: {form.vendorName || "\u2014"}</p><p>PO No.: {form.poNumber || "\u2014"} &middot; {form.poAmount || "\u2014"}</p></>}
                {showInternalPO && (form.vendorName || form.poNumber || form.poDate || form.poAmount) && (
                  <><p>Vendor: {form.vendorName || "\u2014"}</p><p>PO No.: {form.poNumber || "\u2014"} &middot; {form.poAmount || "\u2014"}</p></>
                )}
              </div>
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <button onClick={() => setStep(2)} className="border border-gray-300 text-gray-600 px-4 py-2 rounded-lg text-sm hover:bg-gray-50">&larr; Back</button>
                {draftControls}
                <button onClick={handleSave} disabled={saving} className="bg-green-600 text-white px-6 py-2 rounded-lg text-sm font-semibold hover:bg-green-700 disabled:opacity-50" data-testid="save-log-btn">{saving ? "Saving..." : isEditing ? "Update Log Entry" : "Save Log Entry"}</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function deriveMonthKeyFromDate(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (isNaN(d)) return null;
  const months = ["JAN","FEB","MAR","APR","MAY","JUN","JUL","AUG","SEP","OCT","NOV","DEC"];
  return `${months[d.getMonth()]}-${String(d.getFullYear()).slice(-2)}`;
}

function findLinkedPMCell(entry, pmPlan) {
  if (!entry || (entry.logType || "").toLowerCase() !== "pm") return null;
  const month = entry.pmMonth || deriveMonthKeyFromDate(entry.startDate);
  if (!month) return null;
  const plans = pmPlan || [];

  // A machine usually has multiple PM plans (one per Financial Year).
  // Collect ALL plans whose machine identifier matches this log, then
  // pick the one whose schedule actually contains a cell for this month.
  const matchEntryCode = (entry.machineCode || "").trim();
  const matchEntryId = (entry.machineId || "").trim();
  const candidates = plans.filter(p => {
    const planCode = (p.machineCode || "").trim();
    const planNo = (p.machineNo || "").trim();
    const planId = (p.machineId || "").trim();
    if (matchEntryCode && (planCode === matchEntryCode || planNo === matchEntryCode)) return true;
    if (matchEntryId && planId === matchEntryId) return true;
    return false;
  });

  // First pass: a candidate whose schedule has the exact month key
  for (const plan of candidates) {
    const cell = (plan.schedule || {})[month];
    if (cell && typeof cell === "object") return { plan, month, cell };
  }

  // Second pass (fallback): some legacy cells may be stored as strings even
  // when status is set; treat them as not-found so we don't pretend to have
  // photo/checksheet data when none exists.
  return null;
}

function LogEntryDetail({ entry, onClose, pmPlan, unitId }) {
  const linkedPM = useMemo(() => findLinkedPMCell(entry, pmPlan), [entry, pmPlan]);
  useBackToClose(onClose);
  const [photoList, setPhotoList] = useState([]);
  const [photoLoading, setPhotoLoading] = useState(false);
  const [photoErr, setPhotoErr] = useState("");

  useEffect(() => {
    let cancelled = false;
    setPhotoList([]);
    setPhotoErr("");
    const _cell = linkedPM && linkedPM.cell;
    const _hasR2 = _cell && (_cell.evidencePhotoKey || (Array.isArray(_cell.evidenceAttachments) && _cell.evidenceAttachments.length));
    if (!linkedPM || !_hasR2 || !unitId) return;
    setPhotoLoading(true);
    apiCall("GET", `/pm-plans/${unitId}/${linkedPM.plan.id}/evidence/${encodeURIComponent(linkedPM.month)}/urls`)
      .then(res => {
        if (cancelled) return;
        const list = (res && Array.isArray(res.attachments)) ? res.attachments : [];
        if (list.length) setPhotoList(list);
        else setPhotoErr("Photo unavailable");
      })
      .catch(() => { if (!cancelled) setPhotoErr("Photo unavailable"); })
      .finally(() => { if (!cancelled) setPhotoLoading(false); });
    return () => { cancelled = true; };
  }, [linkedPM, unitId]);

  const checkSheet = linkedPM && linkedPM.cell.checkSheetData;
  const hasChecksheet = checkSheet && Array.isArray(checkSheet.results) && checkSheet.results.length > 0;
  const hasLegacyPhoto = linkedPM && !linkedPM.cell.evidencePhotoKey && linkedPM.cell.evidenceFile;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="p-5 border-b flex justify-between items-start">
          <div><h3 className="font-bold text-gray-900">Log Entry Detail</h3><p className="text-xs text-gray-400 mt-0.5">{entry.startDate} {formatTime12(entry.startTime)}</p></div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl">&times;</button>
        </div>
        <div className="p-5 space-y-4">
          <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold ${entry.workType === "external" ? "bg-orange-100 text-orange-700" : "bg-blue-100 text-blue-700"}`}>
            {entry.workType === "external" ? "External Work" : "Internal Work"}
            {(entry.logType || "").toLowerCase() === "pm" && <span className="ml-1 text-[10px] bg-green-100 text-green-700 font-semibold px-1.5 py-0.5 rounded">Scheduled PM</span>}
          </div>
          <div className="grid grid-cols-2 gap-3 text-sm">
            {[["Start", `${entry.startDate || "\u2014"} ${formatTime12(entry.startTime)}`.trim()], ["End", `${entry.endDate || "\u2014"} ${formatTime12(entry.endTime)}`.trim()], ["Description", entry.description || "\u2014"], ["Detail", entry.detail || "\u2014"], ["Root Cause", entry.rootCause || "\u2014"], ["Rectification", entry.rectification || "\u2014"], ["Spare Part", entry.sparePart ? `${entry.sparePart}${entry.spare_part_id ? " \u2713" : ""}` : "\u2014"], ["Supervisor", entry.supervisor || "\u2014"]].map(([l, v]) => (
              <div key={l} className="bg-gray-50 rounded-lg p-3"><div className="text-xs text-gray-400 mb-0.5">{l}</div><div className="font-medium text-gray-800 text-xs">{v}</div></div>
            ))}
          </div>

          {(entry.logType || "").toLowerCase() === "pm" && (
            <div className="space-y-3 border-t pt-4">
              <p className="text-xs font-semibold text-gray-600 uppercase">Linked PM Plan Record</p>
              {!linkedPM ? (
                <div className="text-xs text-gray-400 bg-gray-50 rounded-lg px-4 py-3">Linked PM cell not found (PM Plan may have been edited or removed).</div>
              ) : (
                <>
                  {/* PM Evidence Photo(s) */}
                  <div className="space-y-1.5">
                    <p className="text-[11px] text-gray-500">PM Evidence {photoList.length > 1 ? `Photos (${photoList.length})` : "Photo"}</p>
                    {(linkedPM.cell.evidencePhotoKey || (Array.isArray(linkedPM.cell.evidenceAttachments) && linkedPM.cell.evidenceAttachments.length)) ? (
                      photoLoading ? (
                        <div className="text-xs text-gray-400 bg-gray-50 rounded-lg px-4 py-6 text-center">Loading photos…</div>
                      ) : photoList.length ? (
                        <div className="flex flex-wrap gap-3">
                          {photoList.map((p, i) => (
                            <a key={i} href={p.url} target="_blank" rel="noopener noreferrer" className="block">
                              <img src={p.url} alt={p.fileName || `PM Evidence ${i + 1}`} className="max-h-48 w-auto rounded-lg border" />
                              <span className="block text-xs text-blue-600 hover:underline truncate max-w-[12rem]">{p.fileName || `Photo ${i + 1}`}</span>
                            </a>
                          ))}
                        </div>
                      ) : (
                        <div className="text-xs text-gray-400 bg-gray-50 rounded-lg px-4 py-3">{photoErr || "Photo unavailable"}</div>
                      )
                    ) : hasLegacyPhoto ? (
                      <a href={linkedPM.cell.evidenceFile} target="_blank" rel="noopener noreferrer" className="block">
                        <img src={linkedPM.cell.evidenceFile} alt="PM Evidence (legacy)" className="max-h-64 w-auto rounded-lg border" />
                        <span className="text-xs text-blue-600 hover:underline">Open full size</span>
                      </a>
                    ) : (
                      <div className="text-xs text-gray-400 bg-gray-50 rounded-lg px-4 py-3">No PM photo recorded</div>
                    )}
                  </div>

                  {/* Filled PM Check Sheet */}
                  <div className="space-y-1.5">
                    <p className="text-[11px] text-gray-500">Filled PM Check Sheet</p>
                    {hasChecksheet ? (
                      <>
                        <div className="text-[11px] text-gray-500">
                          Doc No: <span className="font-semibold text-gray-700">{checkSheet.templateDocNo || "—"}</span>
                          {checkSheet.frequency && <> &middot; Freq: <span className="font-semibold text-gray-700">{checkSheet.frequency}</span></>}
                          {checkSheet.filledAt && <> &middot; Filled: <span className="font-semibold text-gray-700">{new Date(checkSheet.filledAt).toLocaleString("en-IN")}</span></>}
                        </div>
                        <div className="overflow-x-auto border rounded-lg">
                          <table className="w-full text-xs">
                            <thead className="bg-gray-50">
                              <tr>
                                <th className="text-left px-2 py-1.5 font-semibold text-gray-600">#</th>
                                <th className="text-left px-2 py-1.5 font-semibold text-gray-600">Checkpoint</th>
                                <th className="text-left px-2 py-1.5 font-semibold text-gray-600">Status</th>
                                <th className="text-left px-2 py-1.5 font-semibold text-gray-600">Problem Found</th>
                                <th className="text-left px-2 py-1.5 font-semibold text-gray-600">Action Taken</th>
                              </tr>
                            </thead>
                            <tbody>
                              {checkSheet.results.map((r, i) => (
                                <tr key={i} className="border-t">
                                  <td className="px-2 py-1.5 text-gray-400 align-top">{i + 1}</td>
                                  <td className="px-2 py-1.5 text-gray-800 align-top">{r.text || "—"}</td>
                                  <td className="px-2 py-1.5 align-top">
                                    <span className={`px-1.5 py-0.5 rounded text-[11px] font-semibold ${
                                      r.status === "OK" ? "bg-green-100 text-green-700" :
                                      r.status === "NOT OK" ? "bg-red-100 text-red-700" :
                                      r.status === "NA" ? "bg-gray-100 text-gray-600" :
                                      "bg-gray-100 text-gray-500"
                                    }`}>{r.status || "—"}</span>
                                  </td>
                                  <td className="px-2 py-1.5 text-gray-700 align-top">{r.problemFound || "—"}</td>
                                  <td className="px-2 py-1.5 text-gray-700 align-top">{r.action || "—"}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </>
                    ) : (
                      <div className="text-xs text-gray-400 bg-gray-50 rounded-lg px-4 py-3">No filled check sheet for this PM</div>
                    )}
                  </div>
                </>
              )}
            </div>
          )}

          {entry.workType !== "external" && (entry.vendorName || entry.poNumber || entry.poDate || entry.poAmount) && (
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 space-y-2" data-testid="internal-po-view">
              <p className="text-xs font-semibold text-blue-700 uppercase">Purchase Order Details</p>
              <div className="grid grid-cols-2 gap-2 text-xs">
                {[["Vendor", entry.vendorName || "—"], ["PO Number", entry.poNumber || "—"], ["PO Date", entry.poDate || "—"], ["PO Amount", entry.poAmount ? `₹${Number(entry.poAmount).toLocaleString("en-IN")}` : "—"]].map(([l, v]) => (
                  <div key={l} className="bg-white rounded-lg p-2 border border-blue-100"><div className="text-gray-400">{l}</div><div className="font-semibold text-gray-800">{v}</div></div>
                ))}
              </div>
            </div>
          )}
          {entry.workType === "external" && (
            <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 space-y-2">
              <p className="text-xs font-semibold text-orange-700 uppercase">External Vendor Details</p>
              <div className="grid grid-cols-2 gap-2 text-xs">
                {[["Vendor", entry.vendorName || "\u2014"], ["PO Number", entry.poNumber || "\u2014"], ["PO Date", entry.poDate || "\u2014"], ["PO Amount", entry.poAmount ? `\u20B9${Number(entry.poAmount).toLocaleString("en-IN")}` : "\u2014"]].map(([l, v]) => (
                  <div key={l} className="bg-white rounded-lg p-2 border border-orange-100"><div className="text-gray-400">{l}</div><div className="font-semibold text-gray-800">{v}</div></div>
                ))}
              </div>
            </div>
          )}
          <div className="space-y-2">
            <p className="text-xs font-semibold text-gray-600 uppercase">Attached Documents</p>
            {entry.beforeReportFilePath && (
              <a role="button" style={{ cursor: "pointer" }} onClick={(ev) => openProtectedFile(ev, entry.beforeReportFilePath, entry.beforeReportFileName)}
                className="flex items-center gap-3 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 hover:bg-amber-100 cursor-pointer" data-testid="before-report-download">
                <div><p className="text-sm font-medium text-amber-800">{entry.beforeReportFileName}</p><p className="text-xs text-amber-600">Before Maintenance &mdash; click to view/download</p></div>
              </a>
            )}
            {entry.reportFilePath ? (
              <a role="button" style={{ cursor: "pointer" }} onClick={(ev) => openProtectedFile(ev, entry.reportFilePath, entry.reportFileName)}
                className="flex items-center gap-3 bg-green-50 border border-green-200 rounded-lg px-4 py-3 hover:bg-green-100 cursor-pointer" data-testid="report-download">
                <div><p className="text-sm font-medium text-green-800">{entry.reportFileName}</p><p className="text-xs text-green-600">After Maintenance (Work Done) &mdash; click to view/download</p></div>
              </a>
            ) : <div className="text-xs text-gray-400 bg-gray-50 rounded-lg px-4 py-3">No work-done report uploaded</div>}
            {entry.workType === "external" && (
              entry.poFilePath ? (
                <a role="button" style={{ cursor: "pointer" }} onClick={(ev) => openProtectedFile(ev, entry.poFilePath, entry.poFileName)}
                  className="flex items-center gap-3 bg-orange-50 border border-orange-200 rounded-lg px-4 py-3 hover:bg-orange-100 cursor-pointer" data-testid="po-download">
                  <div><p className="text-sm font-medium text-orange-800">{entry.poFileName}</p><p className="text-xs text-orange-600">Purchase Order &mdash; click to view/download</p></div>
                </a>
              ) : <div className="text-xs text-gray-400 bg-gray-50 rounded-lg px-4 py-3">No Purchase Order uploaded</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function PrintableHistoryCard({ machine, entries, printRef }) {
  const MIN_ROWS = 20;
  const blankRowsCount = Math.max(0, MIN_ROWS - entries.length);
  const blankRows = Array(blankRowsCount).fill(null);

  return (
    <div ref={printRef} id="mnt-f04-printable" className="print-container bg-white p-4" style={{ fontFamily: 'Arial, sans-serif' }}>
      <table className="w-full border-2 border-black mb-0" style={{ borderCollapse: 'collapse' }}>
        <tbody>
          <tr>
            <td className="border-2 border-black p-2 w-20 text-center align-middle" rowSpan={2}>
              <img src="/logo.jpg" alt="Logo" className="w-16 h-16 mx-auto object-contain" />
            </td>
            <td className="border-2 border-black p-2 text-center" colSpan={2}>
              <div className="font-black text-lg">Advanced Anmol Metcomp Pvt Ltd</div>
              <div className="font-bold text-base mt-1">Machine History Card</div>
            </td>
            <td className="border-2 border-black p-2 text-xs w-32">
              <div className="mb-1">Doc. No.: <span className="font-bold">MNT-F-04</span></div>
              <div className="mb-1">Rev. No.: <span className="font-bold">00</span></div>
              <div>Date: <span className="font-bold">05.01.2020</span></div>
            </td>
          </tr>
        </tbody>
      </table>
      <div style={{display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:'0', border:'1px solid #000', borderTop:'none', fontSize:'10px'}}>
        <div style={{border:'1px solid #000', padding:'6px'}}><span style={{color:'#666'}}>Machine Desc.</span><br/><strong>{machine.name}</strong></div>
        <div style={{border:'1px solid #000', padding:'6px'}}><span style={{color:'#666'}}>Machine Capacity</span><br/><strong>{machine.capacity}</strong></div>
        <div style={{border:'1px solid #000', padding:'6px'}}><span style={{color:'#666'}}>Department / Section / Line</span><br/><strong>{machine.dept}</strong></div>
        <div style={{border:'1px solid #000', padding:'6px'}}><span style={{color:'#666'}}>Machine No. / ID</span><br/><strong>{machine.machineNo}</strong></div>
        <div style={{border:'1px solid #000', padding:'6px'}}><span style={{color:'#666'}}>Machine Make</span><br/><strong>{machine.make}</strong></div>
        <div style={{border:'1px solid #000', padding:'6px'}}><span style={{color:'#666'}}>Maintenance Frequency</span><br/><strong>{machine.maintenanceFrequency || "6 MONTH"}</strong></div>
        <div style={{border:'1px solid #000', padding:'6px'}}><span style={{color:'#666'}}>Machine Type</span><br/><strong>{machine.name}</strong></div>
        <div style={{border:'1px solid #000', padding:'6px'}}><span style={{color:'#666'}}>Month / Year of Installation</span><br/><strong>{machine.year}</strong></div>
        <div style={{border:'1px solid #000', padding:'6px'}}><span style={{color:'#666'}}>Commissioning Date</span><br/><strong>{machine.commissioningDate || `01/${machine.year}`}</strong></div>
        <div style={{border:'1px solid #000', padding:'6px'}}><span style={{color:'#666'}}>Machine Model</span><br/><strong>{machine.machineModel || machine.machineNo}</strong></div>
        <div style={{border:'1px solid #000', padding:'6px'}}><span style={{color:'#666'}}>Machine Motor Capacity</span><br/><strong>{machine.motorCapacity || machine.capacity}</strong></div>
        <div style={{border:'1px solid #000', padding:'6px'}}><span style={{color:'#666'}}>Machine Manual Availability</span><br/><strong>{machine.manualAvailability || "Available"}</strong></div>
      </div>
      <table className="w-full border-2 border-black border-t-0" style={{ tableLayout: 'fixed', borderCollapse: 'collapse', fontSize: '9px', width: '100%' }}>
        <colgroup>
          <col style={{width:'3%'}} /><col style={{width:'7%'}} /><col style={{width:'5%'}} /><col style={{width:'12%'}} />
          <col style={{width:'12%'}} /><col style={{width:'10%'}} /><col style={{width:'12%'}} /><col style={{width:'8%'}} />
          <col style={{width:'7%'}} /><col style={{width:'5%'}} /><col style={{width:'8%'}} /><col style={{width:'5%'}} /><col style={{width:'6%'}} />
        </colgroup>
        <thead>
          <tr className="bg-gray-200">
            <th className="border border-black p-1 text-center" rowSpan={2}>S.No.</th>
            <th className="border border-black p-1 text-center" colSpan={2}>Start of Incident / Event</th>
            <th className="border border-black p-1 text-center" rowSpan={2}>Description of<br/>Incident / Event</th>
            <th className="border border-black p-1 text-center" rowSpan={2}>Detail of<br/>Incident / Event</th>
            <th className="border border-black p-1 text-center" rowSpan={2}>Root Cause</th>
            <th className="border border-black p-1 text-center" rowSpan={2}>Rectification /<br/>Maint. Detail</th>
            <th className="border border-black p-1 text-center" rowSpan={2}>Spare Part<br/>Used</th>
            <th className="border border-black p-1 text-center" colSpan={2}>End of Incident / Event</th>
            <th className="border border-black p-1 text-center" rowSpan={2}>Sign. Maint.<br/>Supervisor</th>
            <th className="border border-black p-1 text-center" rowSpan={2}>Work<br/>Type</th>
            <th className="border border-black p-1 text-center" rowSpan={2}>PO No.</th>
          </tr>
          <tr className="bg-gray-100">
            <th className="border border-black p-1 text-center">Date</th><th className="border border-black p-1 text-center">Time</th>
            <th className="border border-black p-1 text-center">Date</th><th className="border border-black p-1 text-center">Time</th>
          </tr>
        </thead>
        <tbody>
          {entries.map((e, i) => (
            <tr key={e.id || i}>
              <td className="border border-black p-1 text-center">{i + 1}</td>
              <td className="border border-black p-1 text-center" style={{wordWrap:'break-word', overflow:'hidden'}}>{e.startDate}</td>
              <td className="border border-black p-1 text-center" style={{wordWrap:'break-word', overflow:'hidden'}}>{formatTime12(e.startTime) || ''}</td>
              <td className="border border-black p-1" style={{wordWrap:'break-word', overflow:'hidden'}}>{e.description}</td>
              <td className="border border-black p-1" style={{wordWrap:'break-word', overflow:'hidden'}}>{e.detail || ''}</td>
              <td className="border border-black p-1" style={{wordWrap:'break-word', overflow:'hidden'}}>{e.rootCause || ''}</td>
              <td className="border border-black p-1" style={{wordWrap:'break-word', overflow:'hidden'}}>{e.rectification || ''}</td>
              <td className="border border-black p-1" style={{wordWrap:'break-word', overflow:'hidden'}}>{e.sparePart || ''}</td>
              <td className="border border-black p-1 text-center" style={{wordWrap:'break-word', overflow:'hidden'}}>{e.endDate || ''}</td>
              <td className="border border-black p-1 text-center" style={{wordWrap:'break-word', overflow:'hidden'}}>{formatTime12(e.endTime) || ''}</td>
              <td className="border border-black p-1" style={{wordWrap:'break-word', overflow:'hidden'}}>{e.supervisor}</td>
              <td className="border border-black p-1 text-center" style={{wordWrap:'break-word', overflow:'hidden'}}>{e.workType || ''}</td>
              <td className="border border-black p-1" style={{wordWrap:'break-word', overflow:'hidden'}}>{e.poNumber || ''}</td>
            </tr>
          ))}
          {blankRows.map((_, i) => (
            <tr key={`blank-${i}`}>
              <td className="border border-black p-1 text-center h-6">{entries.length + i + 1}</td>
              {Array(12).fill(null).map((_, j) => <td key={j} className="border border-black p-1 h-6"></td>)}
            </tr>
          ))}
        </tbody>
      </table>
      <div className="mt-4 text-xs text-gray-500 text-center">
        <p>Generated on {new Date().toLocaleDateString('en-IN')} | Machine History Card - MNT-F-04 | Doc No.: MNT-F-04 Rev. No.: 00</p>
      </div>
    </div>
  );
}

export default function HistoryCard({ machine, historyLogs, pmPlan, onBack, onGoToPM, user, unitId, refreshHistory, spareParts, highlightLogId, onClearHighlight, autoResumeDraft = false, onAutoResumed }) {
  const [showModal, setShowModal] = useState(false);
  const [resumeDraft, setResumeDraft] = useState(false);
  const [editEntry, setEditEntry] = useState(null);
  const [viewEntry, setViewEntry] = useState(null);
  const [showPrintPreview, setShowPrintPreview] = useState(false);
  const [exporting, setExporting] = useState(false);
  const printRef = useRef(null);
  const entries = historyLogs.filter(log => log.machineId === machine.id);

  const rowRefs = useRef({});
  const [flashLogId, setFlashLogId] = useState(null);

  useEffect(() => {
    if (!highlightLogId) return;
    const el = rowRefs.current[highlightLogId];
    if (!el) return;
    setFlashLogId(highlightLogId);
    try { el.scrollIntoView({ behavior: "smooth", block: "center" }); } catch (e) { el.scrollIntoView(); }
    const t = setTimeout(() => {
      setFlashLogId(null);
      if (typeof onClearHighlight === "function") onClearHighlight();
    }, 2500);
    return () => clearTimeout(t);
  }, [highlightLogId, entries.length, onClearHighlight]);

  const _curFYNow = new Date();
  const _curFYStartYear = (_curFYNow.getMonth() + 1 >= 4) ? _curFYNow.getFullYear() : _curFYNow.getFullYear() - 1;
  // Start of the current financial year (1 April). PM cells dated before this are from an old FY
  // and must be ignored for the banner. We derive a cell's FY from its MONTH key (see the loop
  // below), NOT from the plan's `fy` field — many legacy plans have no `fy` value at all.
  const _curFYStartDate = new Date(_curFYStartYear, 3, 1);
  const machinePMEntries = (pmPlan || []).filter(p => p.machineCode === machine.machineNo);

  function monthToDate(key) {
    const [m, y] = key.split("-");
    const monthIdx = ["JAN","FEB","MAR","APR","MAY","JUN","JUL","AUG","SEP","OCT","NOV","DEC"].indexOf(m);
    return new Date(2000 + parseInt(y), monthIdx, 1);
  }

  let nextPM = null;
  const now = new Date();
  for (const pmEntry of machinePMEntries) {
    for (const [month, cell] of Object.entries(pmEntry.schedule || {})) {
      const status = typeof cell === "object" ? cell.status : cell;
      if (status !== "PLAN") continue;
      const d = monthToDate(month);
      if (d < _curFYStartDate) continue; // ignore PLAN cells from a previous financial year
      if (!nextPM || d < monthToDate(nextPM.month)) {
        nextPM = { month, freq: pmEntry.freq, planId: pmEntry.id };
      }
    }
  }

  let pmBanner = null;
  if (nextPM) {
    const daysUntil = Math.ceil((monthToDate(nextPM.month) - now) / (1000 * 60 * 60 * 24));
    if (daysUntil < 0) pmBanner = { label: `PM OVERDUE \u2014 was due ${nextPM.month} (${nextPM.freq})`, color: "bg-red-50 border-red-400 text-red-800" };
    else if (daysUntil <= 31) pmBanner = { label: `PM DUE THIS MONTH \u2014 ${nextPM.month} (${nextPM.freq})`, color: "bg-amber-50 border-amber-400 text-amber-800" };
    else if (daysUntil <= 62) pmBanner = { label: `PM UPCOMING \u2014 ${nextPM.month} (${nextPM.freq})`, color: "bg-blue-50 border-blue-400 text-blue-800" };
  }

  // Per-machine reliability (MTBF / MTTR), default range = current financial year.
  const _relNow = new Date();
  const _relFyStart = (_relNow.getMonth() + 1 >= 4) ? _relNow.getFullYear() : _relNow.getFullYear() - 1;
  const [relFrom, setRelFrom] = useState(`${_relFyStart}-04-01`);
  const [relTo, setRelTo] = useState(_relNow.toISOString().split("T")[0]);
  const [relData, setRelData] = useState(null);
  const [relLoading, setRelLoading] = useState(false);
  useEffect(() => {
    let cancelled = false;
    if (!unitId || !machine?.id || !relFrom || !relTo) return undefined;
    setRelLoading(true);
    apiCall("GET", `/reports/reliability/${unitId}?machine_id=${encodeURIComponent(machine.id)}&fromDate=${relFrom}&toDate=${relTo}`)
      .then(res => { if (!cancelled) setRelData(res); })
      .catch(() => { if (!cancelled) setRelData(null); })
      .finally(() => { if (!cancelled) setRelLoading(false); });
    return () => { cancelled = true; };
  }, [unitId, machine, relFrom, relTo]);

  // A draft was only discoverable by reopening the form on the exact machine it was
  // typed on. Surface it on the page instead, and refresh whenever the form closes.
  const [machineDraft, setMachineDraft] = useState(null);
  const refreshMachineDraft = useCallback(() => {
    if (!unitId || !machine?.id) { setMachineDraft(null); return; }
    getDraft(unitId, machine.id).then(setMachineDraft);
  }, [unitId, machine?.id]);
  // Re-read when the form closes, so submitting or discarding clears the pill.
  useEffect(() => { refreshMachineDraft(); }, [refreshMachineDraft, showModal]);

  // Opened from the Drafts section: land in the draft rather than on the machine's
  // history, which is what "continue this draft" is asking for.
  useEffect(() => {
    if (!autoResumeDraft || !machineDraft) return;
    setEditEntry(null);
    setResumeDraft(true);
    setShowModal(true);
    onAutoResumed?.();
  }, [autoResumeDraft, machineDraft, onAutoResumed]);

  async function handleSave() { setShowModal(false); setEditEntry(null); setResumeDraft(false); await refreshHistory(); }
  function handleEdit(entry) { setEditEntry(entry); setResumeDraft(false); setShowModal(true); }
  function handleCloseModal() { setShowModal(false); setEditEntry(null); setResumeDraft(false); }

  async function handleDelete(id) {
    if (window.confirm("Delete this log entry? This cannot be undone.")) {
      try { await apiCall("DELETE", `/history/${unitId}/${id}`); await refreshHistory(); }
      catch (error) { alert("Failed to delete log entry"); }
    }
  }

  function handlePrint() { setShowPrintPreview(true); setTimeout(() => { window.print(); }, 300); }

  async function handleExportPDF() {
    setExporting(true);
    setShowPrintPreview(true);
    await new Promise(resolve => setTimeout(resolve, 500));
    const element = document.getElementById('mnt-f04-printable');
    if (!element) { setExporting(false); return; }
    const opt = {
      margin: [5, 3, 5, 3],
      filename: `MNT-F-04_${machine.machineNo}_${new Date().toISOString().split('T')[0]}.pdf`,
      image: { type: 'jpeg', quality: 1 },
      html2canvas: { scale: 1.5, useCORS: true, logging: false, width: element.scrollWidth, windowWidth: element.scrollWidth },
      jsPDF: { unit: 'mm', format: 'a3', orientation: 'landscape' }
    };
    try { await html2pdf().set(opt).from(element).save(); }
    catch (error) { console.error('PDF export error:', error); alert('Failed to export PDF.'); }
    finally { setExporting(false); setShowPrintPreview(false); }
  }

  const handleDownloadQR = async () => {
    try {
      const baseUrl = window.location.origin;
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/machines/${unitId}/${machine.id}/qr-badge?base_url=${encodeURIComponent(baseUrl)}`,
        { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
      );
      if (!response.ok) throw new Error("Failed to generate QR badge");
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `QR_${machine.machineNo}.png`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("QR badge error:", error);
      alert("Failed to download QR badge");
    }
  };

  const totalExternal = entries.filter(e => e.workType === "external");
  const totalPOValue = totalExternal.reduce((s, e) => s + Number(e.poAmount || 0), 0);

  if (showPrintPreview && !exporting) {
    return (
      <div className="fixed inset-0 bg-white z-50 overflow-auto print:static">
        <div className="no-print p-4 bg-gray-100 border-b flex items-center justify-between sticky top-0">
          <button onClick={() => setShowPrintPreview(false)} className="text-blue-600 hover:underline">&larr; Back to History Card</button>
          <div className="flex gap-2">
            <button onClick={() => window.print()} className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-blue-700">Print Now</button>
            <button onClick={() => setShowPrintPreview(false)} className="border border-gray-300 px-4 py-2 rounded-lg text-sm hover:bg-gray-50">Cancel</button>
          </div>
        </div>
        <div className="p-4 max-w-[297mm] mx-auto">
          <PrintableHistoryCard machine={machine} entries={entries} printRef={printRef} />
        </div>
      </div>
    );
  }

  const hiddenPrintContainer = exporting ? (
    <div className="fixed left-[-9999px] top-0"><PrintableHistoryCard machine={machine} entries={entries} printRef={printRef} /></div>
  ) : null;

  return (
    <div className="space-y-4" data-testid="history-card">
      {hiddenPrintContainer}
      {showModal && <AddLogModal machine={machine} onSave={handleSave} onClose={handleCloseModal} unitId={unitId} editEntry={editEntry} spareParts={spareParts} user={user} resume={resumeDraft} />}
      {viewEntry && <LogEntryDetail entry={viewEntry} onClose={() => setViewEntry(null)} pmPlan={pmPlan} unitId={unitId} />}
      <div className="flex items-center justify-between">
        <button onClick={onBack} className="text-blue-600 text-sm hover:underline flex items-center gap-1 no-print" data-testid="back-to-machines">&larr; Machine List</button>
        <div className="flex gap-2 no-print">
          <button onClick={handleDownloadQR} className="border border-blue-300 text-blue-700 px-3 py-1.5 rounded-lg text-sm font-medium hover:bg-blue-50 flex items-center gap-1.5" data-testid="qr-badge-btn">QR Badge</button>
          <button onClick={handlePrint} className="border border-gray-300 text-gray-700 px-3 py-1.5 rounded-lg text-sm font-medium hover:bg-gray-50 flex items-center gap-1.5" data-testid="print-btn">Print</button>
          <button onClick={handleExportPDF} disabled={exporting} className="bg-red-600 text-white px-3 py-1.5 rounded-lg text-sm font-medium hover:bg-red-700 flex items-center gap-1.5 disabled:opacity-50" data-testid="export-pdf-btn">{exporting ? 'Exporting...' : 'Export PDF'}</button>
        </div>
      </div>
      {pmBanner && (
        <div className={`no-print flex items-center justify-between px-4 py-3 rounded-xl border-l-4 ${pmBanner.color}`} data-testid="pm-banner">
          <span className="font-semibold text-sm">{pmBanner.label}</span>
          <a href="#" onClick={e => { e.preventDefault(); onGoToPM(); }} className="text-xs underline opacity-70 hover:opacity-100">View PM Plan &rarr;</a>
        </div>
      )}
      <div className="no-print bg-white rounded-xl border border-gray-200 p-4" data-testid="machine-reliability">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <p className="text-sm font-semibold text-gray-700">Reliability (MTBF / MTTR)</p>
          <div className="flex items-center gap-2 text-xs">
            <label className="text-gray-500">From</label>
            <input type="date" className="border rounded px-2 py-1" value={relFrom} onChange={e => setRelFrom(e.target.value)} data-testid="rel-from" />
            <label className="text-gray-500">To</label>
            <input type="date" className="border rounded px-2 py-1" value={relTo} onChange={e => setRelTo(e.target.value)} data-testid="rel-to" />
          </div>
        </div>
        {relLoading ? (
          <div className="text-xs text-gray-400 mt-2">Calculating…</div>
        ) : (
          <div className="grid grid-cols-3 gap-3 mt-3">
            <div className="bg-blue-50 rounded-lg p-3 text-center">
              <div className="text-[11px] text-gray-500">MTBF (avg uptime between breakdowns)</div>
              <div className="text-xl font-bold text-blue-700">{relData && relData.summary && relData.summary.mtbfHours != null ? `${relData.summary.mtbfHours} h` : "—"}</div>
            </div>
            <div className="bg-orange-50 rounded-lg p-3 text-center">
              <div className="text-[11px] text-gray-500">MTTR (avg time to repair)</div>
              <div className="text-xl font-bold text-orange-700">{relData && relData.summary && relData.summary.mttrHours != null ? `${relData.summary.mttrHours} h` : "—"}</div>
            </div>
            <div className="bg-gray-50 rounded-lg p-3 text-center">
              <div className="text-[11px] text-gray-500">Breakdowns in period</div>
              <div className="text-xl font-bold text-gray-700">{relData && relData.summary ? (relData.summary.failures || 0) : 0}</div>
            </div>
          </div>
        )}
      </div>
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b">
          <div className="flex items-center gap-3">
            <img src="/logo.jpg" alt="Logo" className="w-10 h-10 rounded-lg object-contain" />
            <div><div className="font-black text-gray-900 text-base">Advanced Anmol Metcomp Pvt Ltd</div><div className="font-bold text-gray-700 text-sm">Machine History Card</div></div>
          </div>
          <div className="text-right text-xs text-gray-500 space-y-0.5">
            <div>Doc. No.: <span className="font-semibold">MNT-F-04</span></div>
            <div>Rev. No.: <span className="font-semibold">00</span></div>
            <div>Date: <span className="font-semibold">05.01.2020</span></div>
          </div>
        </div>
        <div className="grid grid-cols-3 border-b text-xs">
          {[["Machine Desc.", machine.name], ["Machine Capacity", machine.capacity], ["Department / Section / Line", machine.dept],
            ["Machine No. / ID", machine.machineNo], ["Machine Make", machine.make], ["Maintenance Frequency", machine.maintenanceFrequency || "6 MONTH"],
            ["Machine Type", machine.name], ["Month / Year of Installation", `${machine.year}`], ["Commissioning Date", machine.commissioningDate || `01/${machine.year}`],
            ["Machine Model", machine.machineModel || machine.machineNo], ["Machine Motor Capacity", machine.motorCapacity || machine.capacity], ["Machine Manual Availability", machine.manualAvailability || "Available"],
            // Blank rather than a guess: a bed size nobody has measured must not read as
            // one that has been.
            // Blank rather than a guess: a size nobody has measured must not read as one
            // that has been.
            ["Slide Size (L \u00D7 W)", [machine.slideLength, machine.slideWidth].every(Boolean)
              ? `${machine.slideLength} \u00D7 ${machine.slideWidth} mm` : "\u2014"],
            ["Bed Size (L \u00D7 W \u00D7 H)", [machine.bedLength, machine.bedWidth, machine.bedHeight].every(Boolean)
              ? `${machine.bedLength} \u00D7 ${machine.bedWidth} \u00D7 ${machine.bedHeight} mm` : "\u2014"]
          ].map(([l, v]) => (
            <div key={l} className="border-r border-b border-gray-200 px-3 py-2 last:border-r-0">
              <div className="text-gray-400 font-medium">{l}</div><div className="font-semibold text-gray-800 mt-0.5">{v}</div>
            </div>
          ))}
        </div>
        <div className="flex items-center justify-between px-4 py-2.5 bg-gray-50 border-b no-print">
          <div className="flex gap-4 text-xs">
            <span className="text-gray-500">Total Entries: <strong className="text-gray-800">{entries.length}</strong></span>
            <span className="text-blue-600">Internal: <strong>{entries.filter(e => e.workType === "internal").length}</strong></span>
            <span className="text-orange-600">External: <strong>{totalExternal.length}</strong></span>
            {totalPOValue > 0 && <span className="text-green-700">Total PO Value: <strong>{"\u20B9"}{totalPOValue.toLocaleString("en-IN")}</strong></span>}
          </div>
          <div className="flex items-center gap-3">
          {machineDraft && (
            <button
              onClick={() => { setEditEntry(null); setResumeDraft(true); setShowModal(true); }}
              className="inline-flex items-center gap-1.5 rounded-full bg-amber-100 text-amber-800 px-2.5 py-1 text-xs font-semibold hover:bg-amber-200 focus:outline-none focus:ring-2 focus:ring-amber-300"
              title={`Shared draft — ${draftWho(machineDraft)} · last saved ${formatDraftStamp(machineDraft.updatedAt)}. Click to continue it.`}
              data-testid="machine-draft-badge">
              Draft in progress
              <span aria-hidden="true">&rarr;</span>
            </button>
          )}
          {can(user, "addHistory") && (
            <button onClick={() => { setResumeDraft(false); setShowModal(true); }} className="bg-blue-600 text-white text-xs px-4 py-2 rounded-lg font-semibold hover:bg-blue-700 flex items-center gap-1.5" data-testid="add-log-btn">
              <span className="text-base leading-none">+</span> Add Maintenance Log
            </button>
          )}
          </div>
        </div>
        {entries.length === 0 ? (
          <div className="py-16 text-center"><p className="text-gray-500 font-medium">No maintenance logs yet</p><p className="text-xs text-gray-400 mt-1">Click "Add Maintenance Log" to record the first entry</p></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs min-w-[900px]">
              <thead>
                <tr className="bg-gray-100 border-b border-gray-300">
                  <th className="px-3 py-2.5 text-left text-gray-600 font-semibold w-8 border-r border-gray-200">S.No.</th>
                  <th className="px-3 py-2.5 text-center text-gray-600 font-semibold border-r border-gray-200" colSpan={2}>Start of Incident / Event</th>
                  <th className="px-3 py-2.5 text-left text-gray-600 font-semibold border-r border-gray-200">Description of<br/>Incident / Event</th>
                  <th className="px-3 py-2.5 text-left text-gray-600 font-semibold border-r border-gray-200">Detail of<br/>Incident / Event</th>
                  <th className="px-3 py-2.5 text-left text-gray-600 font-semibold border-r border-gray-200">Root Cause</th>
                  <th className="px-3 py-2.5 text-left text-gray-600 font-semibold border-r border-gray-200">Rectification /<br/>Maintenance Detail</th>
                  <th className="px-3 py-2.5 text-left text-gray-600 font-semibold border-r border-gray-200">Spare Part Used<br/>(If Any)</th>
                  <th className="px-3 py-2.5 text-center text-gray-600 font-semibold border-r border-gray-200" colSpan={2}>End of Incident / Event</th>
                  <th className="px-3 py-2.5 text-left text-gray-600 font-semibold border-r border-gray-200">Sign. Maint.<br/>Supervisor</th>
                  <th className="px-3 py-2.5 text-left text-gray-600 font-semibold border-r border-gray-200">Work Type</th>
                  <th className="px-3 py-2.5 text-left text-gray-600 font-semibold border-r border-gray-200">PO No.</th>
                  <th className="px-3 py-2.5 text-left text-gray-600 font-semibold">Docs</th>
                </tr>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="border-r border-gray-200" />
                  <th className="px-3 py-1.5 text-gray-500 font-medium border-r border-gray-200">Date</th>
                  <th className="px-3 py-1.5 text-gray-500 font-medium border-r border-gray-200">Time</th>
                  <th className="border-r border-gray-200" /><th className="border-r border-gray-200" />
                  <th className="border-r border-gray-200" /><th className="border-r border-gray-200" />
                  <th className="border-r border-gray-200" />
                  <th className="px-3 py-1.5 text-gray-500 font-medium border-r border-gray-200">Date</th>
                  <th className="px-3 py-1.5 text-gray-500 font-medium border-r border-gray-200">Time</th>
                  <th className="border-r border-gray-200" /><th className="border-r border-gray-200" /><th className="border-r border-gray-200" /><th />
                </tr>
              </thead>
              <tbody>
                {entries.map((e, i) => (
                  <tr
                    key={e.id}
                    ref={el => { if (el) rowRefs.current[e.id] = el; }}
                    className={`border-b border-gray-100 cursor-pointer transition-colors ${flashLogId === e.id ? "bg-yellow-100 ring-2 ring-yellow-400 ring-inset" : i % 2 === 0 ? "bg-white hover:bg-blue-50" : "bg-gray-50/40 hover:bg-blue-50"}`}
                    onClick={() => setViewEntry(e)}
                  >
                    <td className="px-3 py-2.5 text-gray-400 border-r border-gray-100 text-center">{i + 1}</td>
                    <td className="px-3 py-2.5 whitespace-nowrap border-r border-gray-100 font-medium text-gray-700">{e.startDate}</td>
                    <td className="px-3 py-2.5 whitespace-nowrap border-r border-gray-100 text-gray-600">{formatTime12(e.startTime)}</td>
                    <td className="px-3 py-2.5 border-r border-gray-100 font-medium text-gray-800 max-w-[120px]">
                      <div className="truncate" title={e.description}>{e.description}</div>
                      {(() => {
                        // Effective maintenance type — exactly what the reliability report
                        // counts. A type set by hand wins over the guess from the text.
                        const t = maintenanceTypeOf(e);
                        const b = MT_BADGE[t];
                        const manual = !!e.maintenanceType;
                        return (
                          <span className={`inline-block mt-0.5 text-xs font-semibold px-1.5 py-0.5 rounded ${b.cls}`}
                            title={manual ? `${MT_LABELS[t]} (set manually)` : `${MT_LABELS[t]} (auto-detected from the description)`}
                            data-testid={`log-type-${e.id}`}>
                            {b.text}{manual ? "*" : ""}
                          </span>
                        );
                      })()}
                    </td>
                    <td className="px-3 py-2.5 border-r border-gray-100 text-gray-600 max-w-[120px]"><div className="truncate" title={e.detail}>{e.detail || "\u2014"}</div></td>
                    <td className="px-3 py-2.5 border-r border-gray-100 text-gray-600 max-w-[100px]"><div className="truncate" title={e.rootCause}>{e.rootCause || "\u2014"}</div></td>
                    <td className="px-3 py-2.5 border-r border-gray-100 text-gray-600 max-w-[120px]"><div className="truncate" title={e.rectification}>{e.rectification || "\u2014"}</div></td>
                    <td className="px-3 py-2.5 border-r border-gray-100 text-gray-600">{e.sparePart || "\u2014"}</td>
                    <td className="px-3 py-2.5 whitespace-nowrap border-r border-gray-100 text-gray-600">{e.endDate || "\u2014"}</td>
                    <td className="px-3 py-2.5 whitespace-nowrap border-r border-gray-100 text-gray-600">{e.endTime ? formatTime12(e.endTime) : "\u2014"}</td>
                    <td className="px-3 py-2.5 border-r border-gray-100 font-medium text-gray-700">
                      {e.supervisor}
                      {e.lastEditedBy && <div className="text-xs italic text-gray-400 mt-0.5">Edited by {e.lastEditedBy} on {new Date(e.lastEditedAt).toLocaleDateString('en-IN')}</div>}
                    </td>
                    <td className="px-3 py-2.5 border-r border-gray-100">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-semibold whitespace-nowrap ${e.workType === "external" ? "bg-orange-100 text-orange-700" : "bg-blue-100 text-blue-700"}`}>
                        {e.workType === "external" ? "Ext" : "Int"}
                      </span>
                    </td>
                    <td className="px-3 py-2.5 border-r border-gray-100">
                      {e.poNumber ? <span className="font-mono text-orange-700">{e.poNumber}</span> : <span className="text-gray-300">&mdash;</span>}
                    </td>
                    <td className="px-3 py-2.5" onClick={ev => ev.stopPropagation()}>
                      <div className="flex items-center gap-1.5">
                        {(e.beforeReportFilePath || e.reportFilePath) && <a role="button" style={{ cursor: "pointer" }} onClick={(ev) => openProtectedGallery(ev, [
                          e.beforeReportFilePath ? { path: e.beforeReportFilePath, name: e.beforeReportFileName, label: "Before Maintenance" } : null,
                          e.reportFilePath ? { path: e.reportFilePath, name: e.reportFileName, label: "After Maintenance (Work Done)" } : null,
                        ])} title="View Before / After photos" className="text-blue-500 hover:text-blue-700">RPT</a>}
                        {e.poFilePath && <a role="button" style={{ cursor: "pointer" }} onClick={(ev) => openProtectedFile(ev, e.poFilePath, e.poFileName)} title={e.poFileName} className="text-orange-500 hover:text-orange-700">PO</a>}
                        {!e.beforeReportFilePath && !e.reportFilePath && !e.poFilePath && <span className="text-gray-300">&mdash;</span>}
                        {can(user, "editHistory") && <button onClick={ev => { ev.stopPropagation(); handleEdit(e); }} className="text-blue-400 hover:text-blue-600 ml-1" data-testid={`edit-log-${e.id}`}>Edit</button>}
                        {can(user, "deleteHistory") && <button onClick={ev => { ev.stopPropagation(); handleDelete(e.id); }} className="text-red-300 hover:text-red-500 ml-1" data-testid={`delete-log-${e.id}`}>Del</button>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {totalExternal.length > 0 && (
        <div className="bg-orange-50 border border-orange-200 rounded-xl p-4">
          <h3 className="font-semibold text-orange-800 text-sm mb-3">External Work &mdash; Commercial Tracker</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead><tr className="border-b border-orange-200">{["Date", "Vendor", "Paid By", "PO No.", "PO Date", "Amount", "Report", "Document"].map(h => <th key={h} className="text-left px-3 py-1.5 text-orange-700 font-semibold">{h}</th>)}</tr></thead>
              <tbody>
                {totalExternal.map((e) => (
                  <tr key={e.id} className="border-b border-orange-100 hover:bg-orange-100/50">
                    <td className="px-3 py-2 text-gray-700">{e.startDate}</td>
                    <td className="px-3 py-2 font-medium text-gray-800">{e.vendorName || "\u2014"}</td>
                    <td className="px-3 py-2">
                      <span className={`px-1.5 py-0.5 rounded text-xs font-semibold ${e.paymentMode === "cash" ? "bg-emerald-100 text-emerald-700" : "bg-orange-100 text-orange-700"}`}>
                        {e.paymentMode === "cash" ? "Cash" : "PO"}
                      </span>
                    </td>
                    <td className="px-3 py-2 font-mono text-orange-700">{e.poNumber || "\u2014"}</td>
                    <td className="px-3 py-2 text-gray-600">{e.poDate || "\u2014"}</td>
                    <td className="px-3 py-2 font-semibold text-gray-800">{e.poAmount ? `\u20B9${Number(e.poAmount).toLocaleString("en-IN")}` : "\u2014"}</td>
                    <td className="px-3 py-2">{e.reportFilePath ? <a role="button" style={{ cursor: "pointer" }} onClick={(ev) => openProtectedFile(ev, e.reportFilePath, e.reportFileName)} className="text-blue-500 hover:underline">{e.reportFileName}</a> : <span className="text-gray-300">&mdash;</span>}</td>
                    <td className="px-3 py-2">{e.poFilePath ? <a role="button" style={{ cursor: "pointer" }} onClick={(ev) => openProtectedFile(ev, e.poFilePath, e.poFileName)} className="text-orange-500 hover:underline">{e.poFileName}</a> : <span className="text-gray-300">&mdash;</span>}</td>
                  </tr>
                ))}
                <tr className="bg-orange-100/60">
                  <td colSpan={5} className="px-3 py-2 font-semibold text-orange-800 text-right">Total External Spend (PO + Cash):</td>
                  <td className="px-3 py-2 font-bold text-orange-900">{"\u20B9"}{totalPOValue.toLocaleString("en-IN")}</td>
                  <td colSpan={2} />
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
